import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { useCanonical } from "@/lib/useCanonical";

const Privacy = () => {
  useCanonical({ path: "/privacy" });

  useEffect(() => {
    document.title = "Privacy Policy | Myelina Health";
    const desc =
      "How Myelina Health handles your data across the on-device iOS app and the myelinahealth.com web service. Plain-English disclosure of what is stored, who processes it, and your rights.";
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = desc;
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1 pt-24 pb-16">
        <article className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-3xl">
          <header className="mb-10">
            <h1 className="font-display text-4xl md:text-5xl tracking-tight text-foreground mb-3">
              Privacy Policy | Myelina Health
            </h1>
            <p className="text-sm text-muted-foreground">Last updated: July 11, 2026</p>
          </header>

          <div className="prose prose-sm md:prose-base max-w-none text-muted-foreground space-y-6
            [&_h2]:font-display [&_h2]:text-2xl [&_h2]:text-foreground [&_h2]:tracking-tight [&_h2]:mt-10 [&_h2]:mb-3
            [&_h3]:font-display [&_h3]:text-xl [&_h3]:text-foreground [&_h3]:mt-6 [&_h3]:mb-2
            [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_strong]:text-foreground [&_a]:text-primary [&_a]:underline">

            <p>
              Myelina Health is a quiet companion for people living with multiple sclerosis.
              She lives in two forms, on your iPhone, and on the web, and each one holds
              your data <strong>differently</strong>. This page walks through both, in plain
              language, so you can see exactly where your day goes when you share it.
            </p>
            <ul>
              <li><strong>Part A. The iOS / Apple Watch app.</strong> Works entirely on your device. No account, no servers, your health data does not leave your phone.</li>
              <li><strong>Part B. The web service at myelinahealth.com.</strong> You create an account. Your check-ins, reflections, medications, AI coach conversations, wearable imports, and related data are stored in a secure cloud database so the site can show them back to you.</li>
            </ul>
            <p>
              In both cases: <strong>we do not sell your personal data</strong>, and we never share it with
              data brokers or advertising networks.
            </p>

            <h2>Part A. The iOS / Apple Watch app (on-device)</h2>
            <p>
              The shipped iOS app contains no server endpoints of ours. It works entirely on your device.
            </p>
            <ul>
              <li><strong>No account.</strong> No login, no email required, nothing to sign up for.</li>
              <li><strong>Apple Health, read-only.</strong> With your permission, the app reads heart rate variability, resting and current heart rate, sleep, active energy, and stand time, and, only if you separately enable it, cycle data. It never writes to Apple Health.</li>
              <li><strong>Stored on your device.</strong> Health data is used to compute your energy estimate on your device and stored in an encrypted on-device database. It is not transmitted to us.</li>
              <li><strong>No analytics SDKs, no ad trackers.</strong> Any engagement information the app records stays on your device.</li>
              <li><strong>Optional, weather.</strong> Uses your approximate location to fetch local weather. Not stored by us.</li>
              <li><strong>Optional. Oura Ring.</strong> If you connect one, your Oura access token is stored in the iOS Keychain on your device.</li>
              <li><strong>Sharing is user-initiated.</strong> Wellness Reports and caregiver notes go only where you send them. We do not receive a copy.</li>
              <li><strong>Your control.</strong> Change categories in iOS Settings → Privacy &amp; Security → Health → Myelina Health. Delete the app to delete its data.</li>
            </ul>

            <h2>Part B. The web service (myelinahealth.com)</h2>
            <p>
              The web service is different: it uses an account, and the data you enter is stored in a
              cloud database so we can show it back to you, compute forecasts, and enable optional
              features like AI insights and sharing with a clinician or buddy.
            </p>

            <h3>Account &amp; identifiers</h3>
            <ul>
              <li>Authentication is provided by Supabase Auth (email + password, or Google sign-in).</li>
              <li>We store your email address, a user ID, and the profile fields you provide: first name, MS type, years since diagnosis, energy goals, device list, and notification-time preferences.</li>
              <li>If you subscribe, we also store your Stripe customer ID and subscription status.</li>
              <li>We do not add IP-address or device-fingerprint columns to our own tables. Supabase Auth may retain sign-in metadata (such as IP and user-agent) inside its managed auth logs; that is controlled by Supabase, not by us.</li>
            </ul>

            <h3>Health and personal data we store</h3>
            <p>When you use the web service, the following categories are stored in our database:</p>
            <ul>
              <li><strong>Daily check-ins:</strong> energy level, sleep quality, stress level, brain fog, body symptoms, tags, and any free-text notes you write.</li>
              <li><strong>Evening reflections:</strong> overall energy, prediction accuracy, and evening tags.</li>
              <li><strong>Energy predictions</strong> generated for you (six time-block forecasts, recommendations, day to build rest into, confidence).</li>
              <li><strong>Medications</strong> you log: name, dosage, frequency, time-of-day, and notes.</li>
              <li><strong>Goals and achievements</strong> you set or unlock.</li>
              <li><strong>Weekly plans</strong> generated for you.</li>
              <li><strong>AI Coach conversations:</strong> your messages and the assistant's replies.</li>
              <li><strong>Cached AI Insights:</strong> the periodic pattern summary computed from your data.</li>
              <li><strong>Wearable connections and imports:</strong> OAuth tokens for connected providers (Fitbit / Garmin / Apple exports); imported daily steps, average heart rate, sleep hours, calories, active minutes, and the raw payload.</li>
              <li><strong>Buddy and social features:</strong> buddy pairings, invites, and messages you send.</li>
              <li><strong>Clinician–patient links</strong> (if you connect a clinician): the clinician's email, clinic name, status, and any note.</li>
              {/* Invite-only, no subscription or billing data collected at this time. */}
              <li><strong>Product events:</strong> lightweight funnel events (page path, event name, session ID, referrer host, UTM parameters, device string) to understand how the product is used.</li>
              <li><strong>MCP tool-call logs</strong> (if you connect an AI agent via our MCP integration): which tool was called, when, whether it succeeded, and its arguments.</li>
            </ul>

            <h3>Row-Level Security</h3>
            <p>
              Every user-facing table in our database has Row-Level Security enabled. In plain terms:
              database rules stop one user's data from being returned to another user's session. RLS is a
              safeguard, not a guarantee, see the medical / security disclaimer below.
            </p>

            <h3>Subprocessors, who processes your data on our behalf</h3>
            <p>
              We use the following third-party services to run the web product. Each receives only what
              is necessary for its role.
            </p>
            <ul>
              <li><strong>Supabase (Lovable Cloud).</strong> Our database, authentication, and serverless functions. Stores all of the data listed above.</li>
              <li><strong>Lovable AI Gateway</strong> (ai.gateway.lovable.dev), routing to Google Gemini models. Powers AI Coach, AI Insights, Weekly Plan, Priority Pulse, Rest day detection, and Energy Prediction. <strong>Yes, your health data is sent to this AI gateway when you use those features.</strong> Specifically: your recent daily check-ins (energy, sleep, stress, brain fog, body symptoms, tags, notes), evening reflections, and profile fields (first name, MS type, years since diagnosis), plus the question you type for AI Coach. It is sent so the model can generate an answer for you; we do not use it to train models.</li>
              <li><strong>Stripe.</strong> Payments and subscription management. Receives your email, our user ID, and the price/product you select. Card details are handled by Stripe directly and never touch our servers.</li>
              <li><strong>Emailit</strong> (api.emailit.com). Delivers transactional and authentication emails (magic links, verification, receipts, reminders, buddy notifications). Receives your email address, your first name, and the variables used in the template.</li>
              <li><strong>Wearable providers</strong> (Fitbit, Garmin, or Apple export files), inbound only. If you connect one, we hold an OAuth token so we can read your activity data. We do not push data back to them.</li>
              <li><strong>Google Search Console</strong>. SEO ranking data at the site level. Does not receive user data.</li>
            </ul>
            <p>
              We do not currently use PostHog, Segment, Mixpanel, Sentry, Google Analytics, or advertising
              pixels on the web service.
            </p>

            <h3 id="cookies">Analytics &amp; cookies</h3>
            <p>
              Analytics on myelinahealth.com are first-party only. When you browse the site we may log
              lightweight product events, page path, event name, a session ID, the referring domain,
              UTM parameters from marketing links, and a device string (for example "desktop / Chrome").
              If you are signed in, these events are keyed to your user ID.
            </p>
            <p>
              A cookie banner asks for consent to optional analytics before any of that is recorded. We
              use strictly necessary cookies for sign-in and session handling; anything beyond that is
              opt-in.
            </p>

            <h3>Hosting</h3>
            <p>
              Our database and serverless functions run on Supabase, hosted on Amazon Web Services in the
              <strong> us-east-1 region (United States)</strong>. If you use the web service from outside
              the US, your data is transferred to and stored in the United States.
            </p>

            <h2>Part C. Your rights &amp; choices</h2>

            <h3>Delete your account</h3>
            <p>
              You can delete your account from Settings. When you do, our delete-account function
              purges the following from our database:
            </p>
            <ul>
              <li>Your profile, daily check-ins, evening reflections, medications, goals, achievements, weekly plans, energy predictions, cached AI insights, and AI Coach conversations.</li>
              <li>Your wearable connections, wearable imports, and OAuth state.</li>
              <li>Your push-notification subscriptions.</li>
              <li>Your buddy pairs, buddy invites, buddy messages, and any clinician–patient links you are part of.</li>
              <li>Your subscription record, referral code, invite codes, and product-event history.</li>
              <li>Your MCP tool-call logs and revoked-client entries.</li>
              <li>Your authentication record with Supabase Auth.</li>
            </ul>
            <p>
              A small amount of information is intentionally <strong>retained</strong> after deletion, for
              specific and narrow reasons:
            </p>
            <ul>
              <li><strong>Email deliverability records</strong> (delivery logs, bounces, unsubscribe tokens, suppression list), keyed to your email address. Kept so we honor unsubscribe requests and do not re-mail addresses that have bounced or opted out.</li>
              <li><strong>Newsletter opt-out records</strong>, kept so we can prove you asked not to be contacted.</li>
              <li><strong>Sales / partnership CRM records</strong> (only if you contacted us as a clinician or partner via a lead form), kept for our business records.</li>
            </ul>

            <h3>Access, export, correction</h3>
            <p>
              You can view and edit your profile and logged data inside the app. You can export your
              check-ins and related data as CSV from the Insights page. To request a copy of everything
              we hold, or to ask us to correct something, email us at the address below.
            </p>

            <h3>GDPR / CCPA</h3>
            <p>
              If you are in the EU/UK or California, you have the right to access, correct, delete, and
              port your personal data, and to object to certain processing. Email us to exercise any of
              these rights. <strong>We do not sell personal data</strong> and we do not share it for
              cross-context behavioural advertising.
            </p>

            <h3>Retention</h3>
            <p>
              Your health data on the web service persists until you delete your account. We do not
              currently apply a global auto-expiry to check-ins, reflections, medications, or wearable
              imports. Cached AI Insight summaries are recomputed roughly every 24 hours as your data
              changes. Some invite links, OAuth state, and buddy invites carry their own short
              expiration times.
            </p>

            <h2>Part D. Medical disclaimer</h2>
            <p>
              Myelina Health is a wellness and informational tool. It is <strong>not a medical device</strong>.
              It does not diagnose, treat, cure, or prevent any disease, and its estimates and AI-generated
              suggestions are not medical advice. Always consult a qualified healthcare professional about
              your care.
            </p>

            <h2>Part E. Children</h2>
            <p>
              Myelina Health is intended for adults. It is not directed to children under 13, and we do not
              knowingly collect data from anyone under 13. If you believe a child has created an account,
              contact us and we will delete it.
            </p>

            <h2>Changes to this policy</h2>
            <p>
              We may update this policy as the product evolves. Material changes will show an updated
              "Last updated" date at the top.
            </p>

            <h2>Contact</h2>
            <p>
              Privacy questions or requests: <a href="mailto:hello@myelinahealth.com">hello@myelinahealth.com</a>
            </p>
            <p className="pt-8 text-sm">
              Need help with the app? Visit <Link to="/support">Support</Link>.
            </p>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default Privacy;
